import { toast } from "react-toastify"

import { REQ } from "constants/api/requests"
import { IResponse } from "constants/interfaces"

import useApi, { parseError } from "util/api"

import { LogFilters, LogsResult, Postback, PostbackInput, TestFireResult, TrackingConfig } from "./type"

class PostbacksService {
  public async getConfig(): Promise<TrackingConfig | void> {
    try {
      const { data } = await useApi.get<IResponse<TrackingConfig>>(REQ.ADMIN_DASHBOARD.POSTBACKS.CONFIG)
      if (data.success) return data.response
    } catch {
      toast.error("Failed to load postback config")
    }
  }

  public async updateConfig(payload: TrackingConfig): Promise<TrackingConfig | void> {
    try {
      const { data } = await useApi.patch<IResponse<TrackingConfig>>(REQ.ADMIN_DASHBOARD.POSTBACKS.CONFIG, payload)
      if (data.success) {
        toast.success("Tracking config saved")
        return data.response
      }
    } catch (error) {
      parseError(error, "Failed to save config")
    }
  }

  // --- Postback CRUD ---

  public async list(): Promise<Postback[]> {
    try {
      const { data } = await useApi.get<IResponse<Postback[]>>(REQ.ADMIN_DASHBOARD.POSTBACKS.LIST)
      if (data.success) return data.response
    } catch {
      toast.error("Failed to load postbacks")
    }
    return []
  }

  public async create(payload: PostbackInput): Promise<Postback | void> {
    try {
      const { data } = await useApi.post<IResponse<Postback>>(REQ.ADMIN_DASHBOARD.POSTBACKS.CREATE, payload)
      if (data.success) {
        toast.success("Postback created")
        return data.response
      }
    } catch (error) {
      parseError(error, "Failed to create postback")
    }
  }

  public async update(id: string, payload: PostbackInput): Promise<Postback | void> {
    try {
      const { data } = await useApi.patch<IResponse<Postback>>(REQ.ADMIN_DASHBOARD.POSTBACKS.ITEM(id), payload)
      if (data.success) {
        toast.success("Postback updated")
        return data.response
      }
    } catch (error) {
      parseError(error, "Failed to update postback")
    }
  }

  public async remove(id: string): Promise<boolean> {
    try {
      const { data } = await useApi.delete<IResponse<{ deleted: boolean }>>(REQ.ADMIN_DASHBOARD.POSTBACKS.ITEM(id))
      if (data.success) {
        toast.success("Postback deleted")
        return true
      }
    } catch {
      toast.error("Failed to delete postback")
    }
    return false
  }

  public async test(id: string): Promise<TestFireResult | void> {
    try {
      const { data } = await useApi.post<IResponse<TestFireResult>>(REQ.ADMIN_DASHBOARD.POSTBACKS.TEST(id))
      if (data.success) return data.response
    } catch (error) {
      parseError(error, "Test fire failed")
    }
  }

  // --- Logs + replay ---

  public async getLogs(filters: LogFilters): Promise<LogsResult> {
    try {
      const { data } = await useApi.get<IResponse<LogsResult>>(REQ.ADMIN_DASHBOARD.POSTBACKS.LOGS, {
        params: filters
      })
      if (data.success) return data.response
    } catch {
      toast.error("Failed to load logs")
    }
    return { rows: [], total: 0 }
  }

  public async replay(logId: string): Promise<boolean> {
    try {
      const { data } = await useApi.post<IResponse<unknown>>(REQ.ADMIN_DASHBOARD.POSTBACKS.REPLAY(logId))
      if (data.success) {
        toast.success("Replay queued")
        return true
      }
    } catch (error) {
      parseError(error, "Replay failed")
    }
    return false
  }
}

export const postbacksService = new PostbacksService()
